import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Flex,
  FormFeedback,
} from '@ironhack/design-system2/components';
import { omit, pick } from 'ramda';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCookie, useSessionStorage } from 'react-use';
import * as z from 'zod';

import { CheckboxField } from '@/app/components/client/checkbox-field';
import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { StructuredContent } from '@/app/components/client/structured-content';
import { useFormRequest, useGtmLocalStorage, usePageContext } from '@/hooks';
import { sendEvent } from '@/lib/gtm';
import { regionToCampusMap, urlToDatoLocale } from '@/lib/utils';
import {
  type EmailInput,
  getEmailSchema,
} from '@/lib/utils/form-validations/email-schema';
import {
  type NameInputs,
  getNameSchema,
} from '@/lib/utils/form-validations/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
  getPhoneSchema,
} from '@/lib/utils/form-validations/phone-schema';

import type { ReactElement } from 'react';
import type { Form as FinancingFormType } from '../../modules/1-form-full';

type Phone = PhoneInputs | undefined;

type FormData = {
  newsletter: boolean;
  phone: Phone;
  referral: string;
} & EmailInput &
  NameInputs;

type Props = {
  onSuccess: () => void;
  financingForm: FinancingFormType;
};

const getSchema = (messages: FinancingFormType) =>
  z
    .object({
      newsletter: z.boolean(),
      phone: getPhoneSchema(messages, { isInputActive: messages.withPhone }),
    })
    .merge(getEmailSchema(messages.emailValidationMessages))
    .merge(
      getNameSchema(
        messages.firstNameValidationMessages,
        messages.lastNameValidationMessages
      )
    )
    .required();

export const Form = (props: Props): ReactElement => {
  const { financingForm, onSuccess } = props;
  const {
    buttonText,
    errorMessage,
    legalText,
    newsletterLabel,
    withPhone = false,
  } = financingForm;
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [financingOption] = useSessionStorage('financingOption', 'Ironhack');
  const {
    params: { language, region },
  } = usePageContext();
  const [campus] = useSessionStorage('campus', regionToCampusMap[region][0]);

  const formSchema = getSchema(financingForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      newsletter: false,
      phone: withPhone
        ? getPhoneDefaults(region)
        : // eslint-disable-next-line no-undefined
          undefined,
      referral: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const formRequest = useFormRequest();
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const [countryName] = useCookie('ss_country');

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    try {
      await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          language,
          campus,
          emailOptIn: formData.newsletter,
          financingOption,
        },
        formType: 'financing',
      });

      sendEvent({
        email: formData.email,
        eventAction: 'send financing request',
        eventCategory: 'Financing',
        eventLabel: `${financingOption}::${region}`,
        pageType: 'Financing',
      });
      setGtmLocalStorage({
        applicationData: pick(['email', 'firstName', 'lastName'], formData),
        countryName:
          countryName && countryName !== 'undefined'
            ? countryName
            : urlToDatoLocale[`${region}${language}`].slice(-2),
      });
      onSuccess();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Flex direction="column">
      <FormProvider {...formMethods}>
        <form id="financeForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" direction="column">
            <ContactDetailsFields
              fields={pick(
                [
                  'chooseBetween',
                  'completeFields',
                  'emailLabel',
                  'emailPlaceholder',
                  'firstNameLabel',
                  'firstNamePlaceholder',
                  'lastNameLabel',
                  'lastNamePlaceholder',
                  'phoneLabel',
                  'phonePlaceholder',
                ],
                financingForm
              )}
              mt={4}
              onGoogleClick={() => {
                setFormFeedback(null);
                setIsGoogleSubmitting(true);
              }}
              onGoogleError={() => {
                setIsGoogleSubmitting(false);
                setFormFeedback({ status: 'error', message: errorMessage });
              }}
              onGoogleSuccess={() => {
                setIsGoogleSubmitting(false);
                void handleSubmit(onSubmit)();
              }}
              withPhone={withPhone}
            />
            <CheckboxField
              error={errors.newsletter?.message}
              isRequired
              label={newsletterLabel}
              mt={3}
              name="newsletter"
            />
            <Button
              isLoading={isSubmitting || isGoogleSubmitting}
              mt={3}
              type="submit"
            >
              {buttonText}
            </Button>
            <StructuredContent
              color="text.secondary"
              data={legalText}
              mt={2}
              textAlign="center"
              textStyle="m"
            />
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
